import { useQuery } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';

import { usePermissions, useTimeZonedFunctions } from '/src/hooks';
import { Section } from '../Section';
import { TrendMetric, TrendMetricSection } from '../TrendMetric';
import { TableHeader, TableRow, TableSkeleton } from '../TableSection';

import { webAnalyticsQueryForHomeDash } from '/src/features/web-analytics/queries';
import { pageRowFormatter } from '/src/features/web-analytics/pages/list';
import { round } from '/src/utils/math';
import { msToHumanDuration } from '/src/utils/date';
import { FEATURES } from '/src/features/_global/constants';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { NotInstalledWall } from '/src/features/web-analytics/walls/NotInstalled';
import { OptInWall } from '/src/features/web-analytics/walls/OptIn';
import { TruncateWithTooltip } from '/src/components/TruncateWithTooltip';

import { ReactComponent as VisitorsIcon } from '@crazyegginc/hatch/dist/images/icon-people-outline.svg';
import { SOURCE_TYPE, useSelectedSource } from '/src/components/source-selector/source-selector';

export function VisitorSection({ dateRange }) {
  return (
    <Section className="col-span-2">
      <Section.Header icon={VisitorsIcon} color="text-dodger-blue-500" to="/web-analytics">
        <span>Web Analytics & Instant Heatmaps</span>
      </Section.Header>
      <Section.Body>
        <Content dateRange={dateRange} />
      </Section.Body>
    </Section>
  );
}

function Content({ dateRange }) {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);
  const { selectedSource } = useSelectedSource();

  if (!canViewDashboard) {
    return (
      <div className="p-5">
        <DashboardPaywall feature={FEATURES.WEB_ANALYTICS} reason={reason} mini={true} />
      </div>
    );
  }

  if (!selectedSource) return null;

  if (!selectedSource.installed) {
    return (
      <div className="p-5">
        <NotInstalledWall mini={true} />
      </div>
    );
  }

  if (!selectedSource.webAnalyticsEnabledAt) {
    return (
      <div className="p-5">
        <OptInWall mini={true} />
      </div>
    );
  }

  return <VisitorContent dateRange={dateRange} />;
}

function VisitorContent({ dateRange }) {
  const { selectedSource } = useSelectedSource();
  const { differenceInCalendarDays } = useTimeZonedFunctions();

  const { data, isLoading } = useQuery({
    ...webAnalyticsQueryForHomeDash({
      sourceId: selectedSource.id,
      sourceType: SOURCE_TYPE.SITE,
      startAt: dateRange.start,
      endAt: dateRange.end,
    }),
  });
  const numDays = Math.max(1, differenceInCalendarDays(fromUnixTime(dateRange.end), fromUnixTime(dateRange.start)) + 1);

  return (
    <>
      <TrendMetricSection>
        <TrendMetric
          text="Average unique visitors per day"
          value={data ? Math.round(data.analytics.uniqueVisitors.total / numDays) : null}
          delta={data?.analytics.uniqueVisitors.delta}
          loading={isLoading}
        />
        <TrendMetric
          text="Average session duration"
          value={data?.analytics.avgSessionDuration.total ?? 0}
          delta={data?.analytics.avgSessionDuration.delta}
          loading={isLoading}
          formatFn={(x) => msToHumanDuration(x, false, true)}
        />
        <TrendMetric
          text="Bounce rate"
          value={data ? round(data.analytics.bounceRate.total * 100, 1) : 0}
          delta={data?.analytics.bounceRate.delta}
          loading={isLoading}
          formatFn={(x) => `${x}%`}
          inverseColor={true}
        />
      </TrendMetricSection>
      <TopTraffic data={data} isLoading={isLoading} />
    </>
  );
}

function TopTraffic({ data, isLoading }) {
  const { selectedSource } = useSelectedSource();
  const total = data?.analytics.uniqueVisitors.total ?? 0;

  return (
    <div className="flex">
      <div className="w-1 grow border-r border-mystic-500">
        <TableHeader>Top channels</TableHeader>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          (data?.analytics.channels.data ?? []).slice(0, 3).map((channel, i) => (
            <TableRow key={channel.name} index={i + 1} className="!h-auto min-h-[55px] py-2">
              <div className="grow">{channel.name}</div>
              <div className="ml-4 flex w-14 shrink-0 items-center justify-end text-right">
                {total > 0 ? round((channel.visitors / total) * 100, 1).toLocaleString() : 0}%
              </div>
            </TableRow>
          ))
        )}
      </div>
      <div className="w-1 grow">
        <TableHeader>Top landing pages</TableHeader>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          (data?.analytics.entryPages.data ?? []).slice(0, 3).map((p, i) => (
            <TableRow key={`${p.domain}${p.name}`} index={i + 1}>
              <TruncateWithTooltip text={pageRowFormatter(selectedSource?.name)(p)} className="grow" />
              <div className="ml-4 flex w-16 shrink-0 items-center justify-end text-right">
                {total > 0 ? round((p.visitors / total) * 100, 1).toLocaleString() : 0}%
              </div>
            </TableRow>
          ))
        )}
      </div>
    </div>
  );
}
